import React, { useMemo } from 'react';
import { PropTypes } from 'prop-types';
import { makeStyles, Grid } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForwardSharp';
import { useIntl } from 'react-intl';
import User from '~/modules/common/components/User';
import { FieldLabel } from '~/modules/tasks/components/TaskDrawer/TaskResourceEstimates/TaskAllocationEditor';
import { isoStringToObject } from '~/modules/common/dates/convert';
import { useHasPermission } from '~/modules/common/permissions';
import ProjectTeamMemberDropdown from '../../ProjectTeamMemberDropdown';
import ResourceUsersDropdown from '../../ResourceUsersDropdown';

const useStyles = makeStyles(theme => ({
  label: {
    color: theme.palette.text.secondary
  },
  container: {
    paddingTop: '0px !important',
    backgroundColor: theme.palette.grey[50]
  },
  newUser: {
    minWidth: '45%',
    maxWidth: '54%'
  },
  arrowForwardIcon: {
    paddingTop: `${theme.spacing(2.5)}px !important`,
    marginLeft: 'auto'
  },
  existingResource: {
    maxWidth: '37%'
  }
}));

const useUserStyles = makeStyles(theme => ({
  name: {
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.fontWeightMedium
  },
  container: {
    padding: 0
  }
}));

export const useInputResourceUserDropdownStyles = makeStyles(theme => ({
  popper: {
    width: 'max-content !important'
  }
}));

export const useUserResourceUserDropdownStyles = makeStyles(theme => ({
  label: {
    maxWidth: theme.spacing(13.75)
  }
}));

const ResourceUserSelectionRow = ({
  classes: classesOverride,
  existingResourceUser,
  hideAvailability,
  initialEstimatedHours,
  newResourceUser,
  onResourceUserChange,
  projectId,
  role,
  taskId,
  startDate,
  endDate
}) => {
  const userClasses = useUserStyles();
  const classes = useStyles({ classes: classesOverride });

  const inputResourceUserDropdownClasses = useInputResourceUserDropdownStyles();
  const userResourceUserDropdownClasses = useUserResourceUserDropdownStyles();

  const { formatMessage } = useIntl();

  const selectedResourceIds = useMemo(() => [existingResourceUser.uri], [
    existingResourceUser
  ]);

  const isResourceManager = useHasPermission({
    actionUri: 'urn:replicon-webui:completed-resource-allocation:edit'
  });
  const newResourceLabel = formatMessage({
    id: 'changeTaskResourceDialog.newResource'
  });
  const errorHelperText = formatMessage({
    id: 'changeTaskResourceDialog.noResourceErrorText'
  });

  return (
    <Grid item container className={classes.container}>
      <Grid item className={classes.existingResource}>
        <FieldLabel
          variant="caption"
          messageId="changeTaskResourceDialog.existingResource"
          className={classes.label}
        />
        <User user={existingResourceUser} size="small" classes={userClasses} />
      </Grid>
      <Grid item className={classes.arrowForwardIcon}>
        <ArrowForwardIcon />
      </Grid>
      <Grid item className={classes.newUser}>
        {isResourceManager ? (
          <ResourceUsersDropdown
            autoFocus
            ariaLabel={newResourceLabel}
            assignedRole={role}
            endDate={isoStringToObject(endDate)}
            label={newResourceLabel}
            onResourceChange={onResourceUserChange}
            projectId={projectId}
            startDate={isoStringToObject(startDate)}
            taskId={taskId}
            value={newResourceUser}
            variant="filled"
            hideAvailability={hideAvailability}
            hasError={!newResourceUser}
            helperText={!newResourceUser ? errorHelperText : ''}
            initialEstimatedHours={initialEstimatedHours}
            selectedResourceIds={selectedResourceIds}
            inputClasses={inputResourceUserDropdownClasses}
            userClasses={userResourceUserDropdownClasses}
          />
        ) : (
          <ProjectTeamMemberDropdown
            autoFocus
            assignedRole={role}
            ariaLabel={newResourceLabel}
            label={newResourceLabel}
            onResourceChange={onResourceUserChange}
            projectId={projectId}
            taskId={taskId}
            value={newResourceUser}
            variant="filled"
            hasError={!newResourceUser}
            helperText={!newResourceUser ? errorHelperText : ''}
          />
        )}
      </Grid>
    </Grid>
  );
};

ResourceUserSelectionRow.propTypes = {
  classes: PropTypes.object,
  existingResourceUser: PropTypes.object.isRequired,
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  hideAvailability: PropTypes.bool,
  initialEstimatedHours: PropTypes.number,
  newResourceUser: PropTypes.object,
  onResourceUserChange: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  taskId: PropTypes.string,
  role: PropTypes.object,
  startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
};

export default ResourceUserSelectionRow;
